/*
 * Project: Dynamedics Portal Web
 * Created Date: Thursday February 17th 2022
 * Author: Senath Weerasinghe
 * -----
 * Last Modified: Tuesday March 14th 2023 10:57:33 am
 * Modified By: KasunSKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2022 Mitra Sparks
 * -----
 * HISTORY:
 */

import faker from 'faker';
// utils
import mock from '../utils/mock';
import fakeRequest from '../utils/fakeRequest';
import { fDate } from 'src/utils/formatTime';

// ----------------------------------------------------------------------

let esg = [...Array(24)].map((_, index) => {
  const setIndex = index + 1;
  return {
    id: setIndex,
    uploadedDate: faker.date.past(),
    fileUrl:
      'https://test-experienz-media.s3.eu-west-2.amazonaws.com/experienz/new/experienz_/sample_kpi_sheet.csv',
    dateRange: faker.date.past(),
    documentType: 'esg/kpis',
    uploadedBy: faker.name.firstName() + ' ' + faker.name.lastName(),
    description: 'Data for Q1 Energy',
    status: 5
  };
});

let validateTable = [...Array(24)].map((_, index) => {
  return {
    id: index,
    Segment: 'environment',
    Date: '12-31-2021',
    Sector: 'transport',
    Indicator:
      'Total official development assistance (gross disbursement) for water supply and sanitation, by recipient countries (millions of constant 2019 United States dollars)',
    Indicator_Code: 'Biodiesel HVO',
    Category: 'consumption',
    Sub_Category: 'fuel',
    Company: 'default',
    Department: 'default',
    Location: 'default',
    Data_Type: 'number',
    Data_Unit: '',
    Data: '1200',
    Comparison_Data: '0.5',
    Comparison_Data_Set: '',
    Comparison_Data_Set_Code: 'DC_TOF_WASHL',
    Emission_Factor_Aggregated_Id: 'Biodiesel HVO=l',
    Emission_Factor: 'fuel_type_biodiesel_hvo-fuel_use_na',
    Emission_Factor_Unit: 'kg-CO2e/l'
  };
});

let errorMessages =
  '[{"Date":"12-31-2021","Segment":"social","Sector":"diversity","Indicator_Code":"","Indicator":"Female Employees","Category":"gender","Sub_Category":"male","Company":"default","Location":"default","Department":"default","Data_Type":"percent","Data_Unit":"","Data":"0.6","Comparison_Data":"0.5","Comaprison_Data_Set":"","Comaprison_Data_Set_Code":"","Emission_Factor_Aggregated_Id":"Female Employees=","Emission_Factor":"","Emission_Factor_Unit":"","reasons":{"Comaprison_Data_Set":"Comparison Data Set is not allowed to be empty"}},{"Date":"12-06-2021","Segment":"environment","Sector":"energy","Indicator_Code":"","Indicator":"Renewable energy share in the total final energy consumption","Category":"consumption","Sub_Category":"renewable","Company":"default","Location":"default","Department":"default","Data_Type":"percent","Data_Unit":"","Data":"0.2","Comparison_Data":"0.35","Comaprison_Data_Set":"","Comaprison_Data_Set_Code":"","Emission_Factor_Aggregated_Id":"Renewable energy share in the total final energy consumption=","Emission_Factor":"","Emission_Factor_Unit":"","reasons":{"Comaprison_Data_Set":"Comparison Data Set is not allowed to be empty"}},{"Date":"12-06-2021","Segment":"environment","Sector":"waste","Indicator_Code":"","Indicator":"Waste oils","Category":"disposal","Sub_Category":"oil","Company":"default","Location":"default","Department":"default","Data_Type":"number","Data_Unit":"l","Data":"400","Comparison_Data":"","Comaprison_Data_Set":"","Comaprison_Data_Set_Code":"","Emission_Factor_Aggregated_Id":"Waste oils=l","Emission_Factor":"fuel_type_waste_oils-fuel_use_na","Emission_Factor_Unit":"kg-CO2e/l","reasons":{"Comparison_Data_Set":"Comparison Data Set is not allowed to be empty"}}]';

let ESG_META_INFO = {
  status: 0,
  approved: false
};

const LABELS = {
  Segment: 'Segment',
  Date: 'Date',
  Sector: 'Sector',
  Indicator: 'Indicator',
  Indicator_Code: 'Indicator Code',
  Category: 'Category',
  Sub_Category: 'Sub Category',
  Company: 'Company',
  Department: 'Department',
  Location: 'Location',
  Data_Type: 'Data Type',
  Data_Unit: 'Data Unit',
  Data: 'Data',
  Comparison_Data: 'Comparison Data',
  Comparison_Data_Set: 'Comparison Data Set'
};

const DATA_SETS_META = [
  {
    name: 'Segment',
    options: ['environment', 'social', 'governance'],
    label: 'Segment'
  },
  { name: 'Date', options: [], label: ' Date' },
  { name: 'Sector', options: [], label: 'Sector' },
  {
    name: 'Indicator',
    options: [
      'Hotel stay',
      'Electricity generation – WTT',
      'Grid mix',
      'T&D - UK electricity',
      'Transmission & Distribution – WTT',
      'Domestic air freight',
      'International air freight long haul',
      'International air freight short haul',
      'International air freight outside UK',
      'Rail freight train'
    ],
    label: 'Indicator'
  },

  {
    name: 'Category',
    options: [
      'consumption',
      'disposal',
      'emission',
      'gender',
      'age',
      'education',
      'ethnicity'
    ],
    label: 'Category'
  },
  { name: 'Sub_Category', options: [], label: 'Sub Category' },
  { name: 'Company', options: ['experienz'], label: 'Company' },
  {
    name: 'Department',
    options: [
      'Engineering',
      'BA',
      'Quality Assurance',
      'Project Management Office',
      'Management'
    ],
    label: 'Department'
  },
  { name: 'Location', options: ['experienz HQ'], label: 'Location' },
  {
    name: 'Data_Type',
    options: ['number', 'binary', 'currency'],
    label: 'Data Type'
  },
  {
    name: 'Data_Unit',
    options: ['pnight', 'kWh', 'kg', 'tkm', 'l', 't', 'gj', 'pkm', 'km', 'm3'],
    label: 'Data Unit'
  },
  { name: 'Data', options: [], label: 'Data' }
];

const DEFAULTS_DATA_SETS_META = [
  {
    type: 'Energy Consumption',
    options: [
      {
        name: 'Segment',
        value: 'environment',
        label: 'Segment',
        helperText: 'Segment is s s s s ',
        type: 'default',
        fieldType: 'radio'
      },
      {
        name: 'Sector',
        value: 'energy',
        type: 'default',
        label: 'Sector',
        fieldType: 'radio'
      },
      {
        name: 'Category',
        value: 'consumption',
        type: 'default',
        label: 'Category',
        fieldType: 'radio'
      },
      {
        name: 'Sub_Category',
        value: 'default',
        type: 'default',
        label: 'Sub Category',
        fieldType: 'text'
      },
      {
        name: 'Indicator',
        value: 'Electricity generation – WTT',
        type: 'default',
        label: 'Indicator',
        fieldType: 'radio'
      },
      {
        name: 'Data_Unit',
        value: 'Kwh',
        type: 'default',
        label: 'Data Unit',
        fieldType: 'radio'
      },
      {
        name: 'Data_Type',
        value: 'binary',
        type: 'default',
        label: 'Data Type',
        fieldType: 'radio'
      },
      {
        name: 'Company',
        value: 'experienz',
        type: 'non-default',
        label: 'Company',
        fieldType: 'radio'
      },
      {
        name: 'Location',
        value: 'experienz HQ',
        type: 'non-default',
        label: 'Location',
        hidden: true,
        fieldType: 'radio'
      },
      {
        name: 'Department',
        value: 'BA',
        type: 'non-default',
        label: 'Department',
        hidden: true,
        fieldType: 'radio'
      },
      {
        name: 'Date',
        value: 'k',
        type: 'non-default',
        label: 'Date',
        fieldType: 'date'
      },
      {
        name: 'Data',
        value: 'k',
        type: 'non-default',
        label: 'Data',
        hint: 'Enter the amount of electricity units',
        fieldType: 'text',
        type: 'number',
        endProp: 'Data_Unit'
      }
    ]
  },
  {
    type: 'Financial Data',
    options: []
  }
];

const RAW_DATA_LABELS = {
  SubmissionDate: 'Date of Submission',
  SupplierName: 'Name of the Supplier',
  Item: 'Item',
  Description: 'Description',
  Quantity: 'Quantity',
  Price: 'Price',
  Total: 'Total'
};

let RAW_DATA_RECORDS = [...Array(6)].map((_, index) => {
  return {
    id: index,
    SubmissionDate: fDate(faker.date.past()),
    SupplierName: 'Pepsi',
    Item: 'Laptops',
    Description: '',
    Quantity: '18',
    Price: '$150',
    Total: '2700'
  };
});

const id = 9400;
let pollAttempt = 0;

mock.onGet(/^\/api\/data-sets\/raw\/[^\/]+\/status/).reply(async (request) => {
  try {
    await fakeRequest(4000);
    pollAttempt++;

    if (pollAttempt <= 2) {
      return [
        200,
        {
          data: { id, status: 3, fileSchema: {} }
        }
      ];
    } else if (pollAttempt === 3) {
      const updatedEsg = esg.map((item) => {
        if (item.id === id) {
          return { ...item, status: 6 };
        }
        return item;
      });
      esg = updatedEsg;
      return [
        200,
        {
          data: {
            id,
            status: 6,
            fileSchema: {
              labels: RAW_DATA_LABELS,
              dataRecords: RAW_DATA_RECORDS,
              rowCount: RAW_DATA_RECORDS?.length
            }
          }
        }
      ];
    } else {
      return [
        200,
        {
          data: {
            id,
            status: 6,
            fileSchema: {
              labels: RAW_DATA_LABELS,
              dataRecords: RAW_DATA_RECORDS,
              rowCount: RAW_DATA_RECORDS?.length
            }
          }
        }
      ];
    }
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onPut(/^\/api\/data-sets\/raw\/[^\/]+\/flag/).reply(async (request) => {
  try {
    await fakeRequest(4000);

    return [200, { data: { id: id } }];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onPut(/^\/api\/data-sets\/raw\/[^\/]+/).reply(async (request) => {
  await fakeRequest(3000);
  try {
    const dataId = Number(request.url.split('/').slice(-1)[0]);

    const updatedEsg = esg.map((item) => {
      if (item.id === dataId) {
        return { ...item, status: 5 };
      }
      return item;
    });

    esg = updatedEsg;
    return [200, { dataId }];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onGet('/api/data-sets/meta').reply(async () => {
  return [
    200,
    {
      data: [...DATA_SETS_META]
    }
  ];
});

mock.onGet('/api/data-sets/defaults').reply(async () => {
  return [
    200,
    {
      data: [...DEFAULTS_DATA_SETS_META]
    }
  ];
});

mock.onPost(/^\/api\/data-sets\/confirm\/[^\/]+/).reply(async (request) => {
  await fakeRequest(3000);
  try {
    const { confirmType } = JSON.parse(request.data);
    if (confirmType === 'CONFIRM') {
      ESG_META_INFO.status = 'completed';
    } else {
      ESG_META_INFO.status = 'inactive';
    }
    return [200, { data: [] }];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onGet('/api/data-sets').reply(async () => {
  await fakeRequest(1000);
  esg.sort((a, b) => {
    const dateA = new Date(a.uploadedDate);
    const dateB = new Date(b.uploadedDate);

    return dateB - dateA;
  });
  return [200, { data: { dataFiles: [...esg], labels: LABELS } }];
});

let isValid = false;

mock.onPost('/api/data-sets').reply(async (request) => {
  try {
    isValid = !isValid;
    //uploading
    ESG_META_INFO.status = 1;
    await fakeRequest(4000);
    ESG_META_INFO.status = 3;
    processStateMachine();

    return [200, { data: { id: 1234, executionArn: 'testArn' } }];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

const processStateMachine = async () => {
  await fakeRequest(3000);
  ESG_META_INFO.status = 4;
};

mock.onPut(/^\/api\/data-sets\/confirm\/[^\/]+/).reply(async (request) => {
  await fakeRequest(1000);
  try {
    ESG_META_INFO.status = 0;

    return [200, {}];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onGet(/^\/api\/data-sets\/status\/[^\/]+/).reply(async () => {
  return [
    200,
    {
      data: {
        processState: ESG_META_INFO.status,
        stateMachineStatus: 'RUNNING',
        preview: isValid ? JSON.stringify(validateTable) : errorMessages,
        previewType: isValid ? 'valid' : 'invalid',
        totalRows: 100
      }
    }
  ];
});

mock.onPut(/^\/api\/data-sets\/[^\/]+/).reply(async (request) => {
  await fakeRequest(1000);
  try {
    const dataId = Number(request.url.split('/').slice(-1)[0]);

    return [200, { dataId }];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});

mock.onPost('/api/data-sets/raw').reply(async (request) => {
  try {
    await fakeRequest(1000);
    const newFile = {
      id: 9400,
      uploadedDate: new Date(),
      fileUrl:
        'https://test-experienz-media.s3.eu-west-2.amazonaws.com/experienz/new/experienz_/sample_kpi_sheet.csv',
      dateRange: faker.date.past(),
      documentType: 'Financial Data',
      uploadedBy: faker.name.firstName() + ' ' + faker.name.lastName(),
      description: 'Financial Data for Q2 2023',
      status: 3
    };
    esg = [newFile, ...esg];
    return [
      200,
      {
        data: {
          id: 9400,
          signedUrl:
            'https://66170bfe-fa85-4266-b792-8ff7c09c2aec.mock.pstmn.io/api/digital-content/media'
        }
      }
    ];
  } catch (error) {
    return [500, { message: 'Internal server error' }];
  }
});
